import Rx from 'rlink-rx';
import crc32 from 'crc/lib/crc32';
import logClientChatter from 'rlink-debug-utils/logClientChatter';

import lookupMeta from 'rlink-data-connector/lookupMeta';
import onRlinkEvent from 'rlink-reliclink/onRlinkEvent';
import embedded from 'rlink-data-connector/embedded';

const FETCH_TIMEOUT_WARNING = 1000 * 10;
const FETCH_TIMEOUT_FAILURE = 1000 * 60 * 3;
const pendingSym = Symbol('pending');

export default function clientFetch ({
  callName,
  mapToCallArgs,
  mapResponse = response => response,
  versioned = true,
  bypassVersionParam = false, //used temporarily to get around the version param
  faultyCacheKeys = false,
  assertMissingClientCall = true,
  missingClientCallValue = null,
}) {
  return ({
      fetchStarted,
      fetchCompleted,
      fetchFailed,
      getCaches,
      request,
      requestArgs,
  }) => {
    if (typeof rlinkClient === 'undefined') {
      // just return for now
      // later this shouldn't happen at all
      return;
    }
    let clientCallArgs = mapToCallArgs(...requestArgs);
    let fetchTimeout = null;

    function clearFetchTimeouts () {
      if (fetchTimeout) {
        let [warningTimeout, errorTimeout] = fetchTimeout;
        clearTimeout(warningTimeout);
        clearTimeout(errorTimeout);
        fetchTimeout = null;
      }
    }

    function makeClientCall (type) {
      let {version: haveVersion = -1, pending} = lookupMeta(
        getCaches(), request, ...requestArgs);
      let callInspect = `rlinkClient.${callName}(${(!bypassVersionParam) ? haveVersion : 'BYPASSED'}, ${clientCallArgs.map(JSON.stringify).join(', ')})`;
      if (type === 'FETCH' && pending) {
        logClientChatter(`[${type}] PENDING already: ${callInspect}`);
        return [pendingSym, callInspect];
      }
      logClientChatter(`[${type} ] >CALL ${callInspect}...`);

      let response = null;
      try {
        response = (bypassVersionParam)
        ? rlinkClient[callName](...clientCallArgs)
        : rlinkClient[callName](haveVersion, ...clientCallArgs);
      } catch (e) {
        if (!assertMissingClientCall) {
          logClientChatter(`[${type}] RESOLVED ${callInspect}: Call does not exist in this client version...`);
          fetchCompleted(embedded(request, requestArgs, mapResponse(missingClientCallValue), {haveVersion}));
          clearFetchTimeouts();
          return [pendingSym, callInspect];
        } else {
          let err = `On Client Call Type ${type}, CallName: ${callName} doesn't exist`;
          throw new Error(err);
        }
      }

      logClientChatter(`[${type}] <CALL ${callInspect}: response=`, response);
      let {
        data, hasData, same, version, cacheID,
      } = response;

      if (!bypassVersionParam && (typeof cacheID !== 'string' || cacheID.length === 0)) {
        let err = `[${type}] INVALID cacheID for ${callInspect}: ${cacheID}`;
        fetchFailed(err);
        throw new Error(err);
      }

      if (versioned && hasData && version === -1) {
        let err = `[${type}] INVALID version for ${callInspect}: ${cacheID}. This call is probably not versioned, use clientFetch({ versioned: false, ... }).`;
        fetchFailed(err);
        throw new Error(err);
      }

      // Unversioned APIs don't provide correct same / version flags
      // in the response, so we compute our own
      if (!versioned && hasData) {
        version = crc32(JSON.stringify(data));
        same = (haveVersion === version);
      }

      if (same) {
        logClientChatter(`[${type}] SAME data receieved for ${callInspect}:`, response);
        if (type === 'EVENT' && versioned) {
          console.warn('WARNING: RLink Client has posted an event for data we already have. This should not normally happen. Notify someone on the RLink team.');
        }
        clearFetchTimeouts();
      } else {
        if (hasData) {
          logClientChatter(`[${type}] RESOLVED ${callInspect}:`, response, hasData, same, version, data);
          fetchCompleted(embedded(request, requestArgs, mapResponse(data), {version}));
          clearFetchTimeouts();
        } else {
          logClientChatter(`[${type}] START fetch for ${callInspect}`, response);
          if (type === 'EVENT') {
            console.warn('WARNING: RLink Client has posted an event but no data is present in the client. This should not normally happen. Notify someone on the RLink team.');
          }
          if (!fetchTimeout) {
            fetchTimeout = [
              setTimeout(() => console.warn(`[${type}] SLOW WARNING: ${callInspect} is taking too long and will likely fail.`), FETCH_TIMEOUT_WARNING),
              setTimeout(() => {
                let err = `[${type}] TIMEOUT: ${callInspect} has timed out.`;
                console.error(err);
                fetchFailed(err);
              }, FETCH_TIMEOUT_FAILURE),
            ];
          }
          fetchStarted();
        }
      }
      return [cacheID, callInspect];
    }


    let [cacheID, callInspect] = makeClientCall('FETCH');
    if (cacheID !== pendingSym) {
      let eventConditions = {cacheID};
      if (!faultyCacheKeys) {
        eventConditions.cacheKeys = jsonKeys => jsonKeys.some(
          jsonKey => jsonKey === JSON.stringify(clientCallArgs));
      }
      onRlinkEvent(eventConditions, ({errorCode, extendedError, jobID, ...rest}) => {
        logClientChatter(`[EVENT] EVENT from ${callInspect}:`, errorCode, extendedError, jobID, rest);

        if (errorCode) {
          let err = `[EVENT] ERROR from ${callInspect}: ${errorCode} - ${extendedError}`;
          logClientChatter(err);
          clearFetchTimeouts();
          fetchFailed(err);
          throw new Error(err);
        }

        //eslint-disable-next-line no-unused-vars
        let [newCacheID, _] = makeClientCall('EVENT');

        if (newCacheID !== cacheID) {
          console.warn(`WARNING! RLink client call has switched cacheIDs on us! This should NEVER happen! ${newCacheID} !== ${cacheID}`);
        }
      }, request.keyFor(...requestArgs));

    }
  };
}



// WEBPACK FOOTER //
// ./lib/rlink-data-connector/fetching/clientFetch.js